// src/components/UI/AnimalProfile.tsx
import { isFulfilled } from "@reduxjs/toolkit";
import { useLoader } from "contexts/LoaderContext";
import { useDeviceType } from "hooks/useDeviceType";
import React from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { getPetOutputForAgent } from "services/AgentSaveHandler";
import { isAgent, isLandlord, isTenant, saveTenantPetData, updateTenantItemByUid } from "services/TenantSubmitHandler";
import { useAppDispatch, useAppSelector } from "store/configureStore";
import { setTenantItems } from "store/tenant/slice";
import { savePetDecisionByAgent } from "store/tenant/thunks";
import { TenantPetsData, TenantPetsDocument } from "store/tenant/types";
import { removeElementsByClass, renumberFieldsByAnimalType } from "utils/childrenProcessor";
import { isQldRegion } from "utils/petFormHelpers";
import { collectFieldNames } from "utils/renderTemplates";
import { transformAnimalFormData } from "utils/splitDot";
import { IconName } from "../AnimalCard/index";
import Badge from "../UI/Badge";
import Button from "../UI/Button";
import Icon from "../UI/Icon/Icon";

/**
 * Props for the AnimalProfile component.
 *
 * @property {string} title - The title to display for the animal profile.
 * @property {IconName} type - The icon type representing the animal.
 * @property {() => void} [onRemove] - Optional callback invoked when the profile is removed.
 * @property {React.ReactNode} children - The content to render inside the profile.
 */
export interface AnimalProfileProps {
  petId?: string | null;
  type: IconName;
  title: string;
  onRemove: (profileMainTitle: string, profileSubHeaderText: string, uid: string) => void;
  animalType: IconName;
  children?: React.ReactNode;
  petData?: TenantPetsData[];
  petDocumentData?: TenantPetsDocument[];
  roleType?: string;
  collapse?: boolean;
  onOpen?: () => void;
  activeState?: number;
  approvalStatus?: string;
  conditionStatus?: string;
  keyIndex?: number;
  handleViewProfile?: (
    type: string,
    idx: number,
    petData: any,
    petDocuments: any[]
  ) => void;
  isModal?: boolean;
  profileHeaderText?: string;
  unique_link?: string;
  tenant_link?: string;
  animal_profile_id?: string;
  onProfileSaved?: (animal_profile_id: number) => void;
  uid?: number;
  maxHight?: boolean;
  /**
   * Optional callback to refresh tenant details after saving pet data.
   * @returns void
   */
  onRefreshAfterSavePet?: () => void;
}

/**
 * AnimalProfile component displays a profile card for an animal, including its title, type, and status.
 *
 * @param {AnimalProfileProps} props - The properties for the AnimalProfile component.
 * @param {string} props.title - The title or name of the animal profile.
 * @param {string} props.type - The type of animal, used to determine the icon.
 * @param {() => void} props.onRemove - Callback function triggered when the remove button is clicked.
 * @param {React.ReactNode} props.children - Child components or elements to be rendered within the profile card.
 *
 * @returns {JSX.Element} The rendered animal profile card component.
 *
 * @remarks
 * - The profile card can be expanded or collapsed by clicking the chevron icon.
 * - Displays a badge indicating the profile status and a button to remove the profile.
 */
const AnimalProfileModal: React.FC<AnimalProfileProps> = ({
  petId,
  title,
  type,
  onRemove,
  children,
  animalType,
  petData,
  petDocumentData,
  roleType,
  collapse,
  onOpen,
  activeState,
  keyIndex,
  approvalStatus,
  conditionStatus,
  handleViewProfile,
  isModal = "false",
  profileHeaderText,
  unique_link = "",
  tenant_link = "",
  animal_profile_id = "",
  onProfileSaved,
  uid,
  onRefreshAfterSavePet,
  maxHight
}) => {
  // Use collapse prop to control open/close
  const isOpen = !collapse;
  
  const disptach = useAppDispatch()
  const { showLoader, hideLoader } = useLoader();
  const uploadedDocumentsList = useAppSelector((state) => state.formTemplate.uploadedDocuments);
  const tenantItems = useAppSelector((state) => state.Tenant.tenantItems);
  const deviceType = useDeviceType();
  const formContext = useFormContext();
  const region = localStorage.getItem("pet_app_region") || "nsw"; // Default to NSW if region is not set
  if (!formContext) {
    return null; // or render a fallback UI
  }
  
  const { watch, formState, trigger, getValues } = formContext;

  const requiredFields = collectFieldNames(children);
  const allFields = watch();
  const errors = formState.errors;



  // Utility to get nested value by path string (e.g., 'dog.is_more_than_one.0')
  function getValueByPath(obj: any, path: string) {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  }

    // Set the profile header text label based on user role and animal count
  let profileHeaderTextLabel = "";

  if (isAgent() || isLandlord()) {
    // For agent/landlord, use petData to show total animal count
    const count = petData?.[0]?.animal_count ?? 1;
    profileHeaderTextLabel = `Profile applies to ${count} ${(String(type) === "smallMammal") ? "small mammal" : (String(type) === "other") ? "animal" : type}${count > 1 && String(type) !== "fish" ? "s" : ""} in total`;
  } else {
    // For tenant, get count and pluralize if needed
    // QLD has no amount selector so always hide this text
    if (isQldRegion()) {
      profileHeaderTextLabel = "";
    } else {
      const isMultiple = watch(`${type}.is_more_than_one.${keyIndex}`, false);
      const count = isMultiple ? watch(`${type}.animal_count.${keyIndex}`, 2) : 1;
      profileHeaderTextLabel =
      profileHeaderText ||
      `Profile applies to ${count} ${(String(type) === "smallMammal") ? "small mammal" : (String(type) === "other") ? "animal" : type}${count > 1 && String(type) !== "fish" ? "s" : ""} in total`;
    }
  }

  let profileType = "";
  if(isAgent() || isLandlord()){
    const customType = petData?.[0]?.custom_type ?? "";
    profileType = isQldRegion() && !customType ? title : (title + ": " + customType);
  } else {
    const customType = watch(`${type}.type_of_animal.${keyIndex}`) || petData?.[0]?.custom_type || "";
    profileType = isQldRegion() && !customType ? title : (title + ": " + customType);
  }





    // Check if all required fields are filled, considering microchip/choices logic
    const isKnownOptionalField = (fieldPath: string) => {
      const fieldName = fieldPath.toLowerCase();
      return fieldName.includes('supporting') ||
             fieldName.includes('optional') ||
             fieldName.includes('description2') ||
             fieldName.includes('fur_colour_length') ||
             fieldName.includes('weight_height_length') ||
             fieldName.includes('.description.') ||
             fieldName.includes('reason_for');
    };

    let allFilled = false;
    if (requiredFields.length > 0) {
      // Find microchip and registration field indices (NSW uses 'choices', QLD uses 'registration_number')
      const microchipIdx = requiredFields.findIndex((f: string) => f.includes("microchipNumber"));
      let registrationIdx = requiredFields.findIndex((f: string) => f.includes("choices"));
      if (registrationIdx === -1) {
        registrationIdx = requiredFields.findIndex((f: string) => f.includes("registration_number"));
      }
      if (microchipIdx !== -1 && registrationIdx !== -1) {
        // If both present, only one needs to be filled
        const microchipValue = getValueByPath(allFields, requiredFields[microchipIdx]);
        const registrationValue = getValueByPath(allFields, requiredFields[registrationIdx]);
        // Check other required fields (excluding microchip/registration and known optional fields)
        const otherFieldsFilled = requiredFields.every((fieldPath: string, idx: number) => {
          if (idx === microchipIdx || idx === registrationIdx) return true;
          if (isKnownOptionalField(fieldPath)) return true;
          const value = getValueByPath(allFields, fieldPath);
          return value !== undefined && value !== null && value !== "";
        });
        allFilled = ((microchipValue !== undefined && microchipValue !== null && microchipValue !== "") ||
                    (registrationValue !== undefined && registrationValue !== null && registrationValue !== "")) && otherFieldsFilled;
      } else {
        // Normal required field check
        allFilled = requiredFields.every((fieldPath: string) => {
          if (isKnownOptionalField(fieldPath)) return true;
          const value = getValueByPath(allFields, fieldPath);
          return value !== undefined && value !== null && value !== "";
        });
      }
    }

    // Check if any required field has a validation error
    let hasError = false;
    if (requiredFields.length > 0) {
      const microchipIdx = requiredFields.findIndex((f: string) => f.includes("microchipNumber"));
      let registrationIdx = requiredFields.findIndex((f: string) => f.includes("choices"));
      if (registrationIdx === -1) {
        registrationIdx = requiredFields.findIndex((f: string) => f.includes("registration_number"));
      }
      if (microchipIdx !== -1 && registrationIdx !== -1) {
        // Only check error for both if both present
        const microchipError = !!getValueByPath(errors, requiredFields[microchipIdx]);
        const registrationError = !!getValueByPath(errors, requiredFields[registrationIdx]);
        // If both have error, mark as error
        hasError = microchipError && registrationError;
        // Check other required fields
        hasError = hasError || requiredFields.some((fieldPath: string, idx: number) => {
          if (idx === microchipIdx || idx === registrationIdx) return false;
          return !!getValueByPath(errors, fieldPath);
        });
      } else {
        hasError = requiredFields.some((fieldPath: string) => !!getValueByPath(errors, fieldPath));
      }
    }

    // Final isComplete logic
    const isComplete = allFilled && !hasError;

    const profileMainTitle = type !== "dog" && type !== "cat" && type !== "fish" && profileType ? profileType : title;
    const profileSubHeaderText = (type === "dog" || type === "cat") ? "" : profileHeaderTextLabel ?? "";
  
  

  const handleSaveProfile = async () => {
    showLoader();
  const requiredFields = collectFieldNames(children);
    const isValid = await trigger(requiredFields);
    const allValues = getValues();
    const profileData = requiredFields.reduce<Record<string, any>>((acc, field) => {
      acc[field] = getValueByPath(allValues, field);
      return acc;
    }, {});
    const payload: Record<string, any> = {};
    const requiredFormat: Record<string, any> = transformAnimalFormData(profileData, uploadedDocumentsList, uid, tenantItems);
    
    payload['pets'] = requiredFormat || [];
    payload["unique_link_token"] = tenant_link  || "";
    payload["tenant_link"] = unique_link || "";

    if (isValid) {
      const savePetResponse = await disptach(saveTenantPetData(payload, unique_link));
      if (onProfileSaved && typeof uid === "number") {
        onProfileSaved(uid);
      }

      if(isFulfilled(savePetResponse)) {
        if (typeof uid === "number") {
          const updatedItems = updateTenantItemByUid(tenantItems ?? [], uid, { pet_id: savePetResponse?.payload?.data?.latest_pet_id });
          await disptach(setTenantItems(updatedItems));
        }
        toast.success("Profile saved successfully");
      }
      // Callback to refresh tenant details in parent component
      onRefreshAfterSavePet && onRefreshAfterSavePet();

      if (onOpen) {
        onOpen();
      }
    }
    hideLoader();
  };
  
  const handleSavePetDecision = async() => {
    showLoader();
    const requiredFields = collectFieldNames(children);
    const isValid = await trigger(requiredFields);
    
    const allValues = getValues();
    const profileData = requiredFields.reduce<Record<string, any>>((acc, field) => {
      acc[field] = getValueByPath(allValues, field);
      return acc;
    }, {});

    const petRequiredFormat = getPetOutputForAgent(profileData, petId ?? "");
    const payload: Record<string, any> = {};
    payload.pets = [petRequiredFormat];

    const urlParts = window.location.pathname.split("/");
    const agentId = urlParts?.[2];
    const agentLink = urlParts?.[4];

    if(isValid){
      try {
        showLoader();
        const response = await disptach(savePetDecisionByAgent({ agentId, applicationId: agentLink, agentResponseData: payload }));
        
        if(isFulfilled(response)) {
          // Pet decision saved successfully
        }
      } catch (error) {
        // Error saving pet decision
      }

      if (onOpen) {
        onOpen();
      }
    }
    
    hideLoader();
  }

  return (
    <div className={`border border-grey-3 p-20px radius-5px drop-shadow d-flex flex-column gap-20px modal-body ${maxHight && "max-h-70vh overflow-auto"}`}>
      <div className="bg-light-blue p-10px radius-5px min-h-md-70px d-flex flex-column flex-md-row gap-10px gap-md-30px align-items-md-center cursor-pointer"
      onClick={() => {
              if (onOpen) {
              onOpen();
              }
            }}>
        <div className="d-flex gap-10px gap-md-30px align-items-center">
          {(isModal === "false" || isModal === false) && (
            <div
            className="d-flex text-grey-2 hover:text-black gap-8px align-items-center w-auto w-md-100px cursor-pointer"
            
            >
              <Icon name={isOpen ? "chevronUp" : "chevronDown"} />
              <div className="fs-16px fw-bold  d-none d-md-block">
                {isOpen ? "Collapse" : "Expand"}
              </div>
            </div>
          )}
          <div className="d-flex gap-10px align-items-center">
            {/* Show icon if isModal is true for both mobile and desktop */}
            {isModal === true && deviceType === "mobile" && (
              <Icon name={type} size={40} className="min-w-40px " />
            )}

             { deviceType !== "mobile" && (
              <Icon name={type} size={40} className="min-w-40px " />
            )}

            
            
            <div className="d-flex flex-column">
              <div className="fs-18px fw-bold text-black text-break">{profileMainTitle}</div>
              <div className="fs-14px fw-normal text-black"> {region !== "qld" && profileSubHeaderText} </div>
            </div>
          </div>          
        </div>
        {(isAgent() || isLandlord()) && activeState !== 1 && (
          <div className="d-block d-md-none">
            <Button
              type="button"
              className="btn-theme text-black hover:text-black  bg-medium-blue"
              iconName="attachment"
              // iconColor="grey-2"
              text={"View Pet Info"}
              onClick={e => {
                e.stopPropagation();
                handleViewProfile &&
                  handleViewProfile(
                    animalType,
                    keyIndex || 0,
                    petData && petData.length > 0 ? petData[0] : null,
                    petDocumentData ?? []
                  );
              }}
            />
          </div>
          )}
        <div className="bg-lines min-h-1px d-block d-md-none"></div>
        <div className="d-flex flex-wrap gap-10px align-items-md-center flex-fill justify-content-center">
          {isTenant() && (
            <Badge color={isComplete ? "blue" : "grey-1"}>
              {isComplete ? "Complete" : "Incomplete"}
            </Badge>
          )}

          {(roleType === "agent" || roleType === "landlord") && activeState !== 1 && (
            <>
              {approvalStatus === "approved" && (
                <Badge color="navy">Approved</Badge>
              )}
              {conditionStatus === "no-condition" && (
                <Badge color="grey-2">No Conditions</Badge>
              )}
              {conditionStatus === "condition-apply" && (
                <Badge color="blue">Conditions Apply</Badge>
              )}
              {approvalStatus === "declined" && (
                <Badge color="red">Declined</Badge>
              )}
              {approvalStatus === "incomplete" && (
                <Badge color="grey-1">Incomplete</Badge>
              )}
            </>
          )}

          {isTenant() && (
            <Button
              type="button"
              className="btn-theme px-0 h-auto text-grey-2 hover:text-black ms-auto bg-transparent"
              iconName="remove"
              // iconColor="grey-2"
              text="Remove"
              onClick={() => onRemove(profileMainTitle, profileSubHeaderText, String(uid ?? ""))}
            />
          )}

          {(isAgent() || isLandlord()) && activeState !== 1 && (
            <div className="d-none d-md-block ms-auto">
            <Button
              type="button"
              className="btn-theme text-black hover:text-black  bg-medium-blue"
              iconName="attachment"
              // iconColor="grey-2"
              text={"View Pet Info"}
              onClick={e => {
                e.stopPropagation();
                handleViewProfile &&
                  handleViewProfile(
                    animalType,
                    keyIndex || 0,
                    petData && petData.length > 0 ? petData[0] : null,
                    petDocumentData ?? []
                  );
              }}
            />
            </div>
          )}
        </div>
      </div>
      {<div className={isOpen ? "d-contents" : "d-none"}>{renumberFieldsByAnimalType(removeElementsByClass(children, 'flk-agent-line'), type)}</div>}
      {isOpen && isTenant() && (
      <>
        <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">
          <Button
            type="button"
            className="btn-theme bg-grey-1 text-white"
            iconName="check"
            iconPosition="left"
            // iconColor="grey-2"
            text="Save Profile"
            onClick={handleSaveProfile}
          />
        </div>
      </>
      )}

      {isOpen && isAgent() && activeState !== 1 && (
      <>
        <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">
          <Button
            type="button"
            className="btn-theme bg-grey-1 text-white hover:bg-grey-2 disabled:bg-disabled"
            iconName="check"
            iconPosition="left"
            // iconColor="grey-2"
            text="Save"
            disabled={!isComplete}
            onClick={handleSavePetDecision}
          />
        </div>
      </>
      )}
    </div>
  );
};

export default AnimalProfileModal;
